import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Table, 
  Button, 
  Card, 
  Tag, 
  Space, 
  Input, 
  Select, 
  Popconfirm, 
  message,
  Breadcrumb,
  Tooltip
} from 'antd';
import { 
  ArrowLeftOutlined, 
  ReloadOutlined, 
  StopOutlined, 
  PlayCircleOutlined,
  DeleteOutlined,
  SearchOutlined,
  CheckCircleOutlined,
  LockOutlined,
  UnlockOutlined
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;
const { Search } = Input;

const InstalledAppsList = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  
  const [device, setDevice] = useState(null);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [operating, setOperating] = useState(false);
  const [uninstallingApp, setUninstallingApp] = useState(false);

  useEffect(() => {
    fetchDeviceAndApps();
  }, [deviceId]);

  const fetchDeviceAndApps = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/devices/${deviceId}`);
      if (response.data.success) {
        setDevice(response.data.data);
        setApps(response.data.data.installedApps || []);
      } else {
        message.error('获取设备信息失败');
      }
    } catch (error) {
      message.error('获取设备信息失败');
      console.error('Fetch device error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 过滤应用列表
  const filteredApps = apps.filter(app => {
    // 搜索过滤
    const searchMatch = !searchText || 
      app.appName?.toLowerCase().includes(searchText.toLowerCase()) ||
      app.packageName?.toLowerCase().includes(searchText.toLowerCase());
    
    // 状态过滤
    let statusMatch = true;
    if (statusFilter === 'enabled') {
      statusMatch = !app.disabled;
    } else if (statusFilter === 'disabled') {
      statusMatch = !!app.disabled;
    }
    
    return searchMatch && statusMatch;
  });

  // 单个禁用应用
  const handleHideApp = async (packageName) => {
    setOperating(true);
    try {
      const response = await api.post('/commands/hide-app', {
        deviceId,
        packageName
      });

      if (response.data.success) {
        message.success('禁用应用指令已发送');
        setApps(prev => prev.map(app => 
          app.packageName === packageName ? { ...app, disabled: true } : app
        ));
      } else {
        message.error(response.data.message || '发送禁用应用指令失败');
      }
    } catch (error) {
      message.error('发送禁用应用指令失败');
      console.error('Hide app error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 单个启用应用
  const handleShowApp = async (packageName) => {
    setOperating(true);
    try {
      const response = await api.post('/commands/show-app', {
        deviceId,
        packageName
      });

      if (response.data.success) {
        message.success('启用应用指令已发送');
        setApps(prev => prev.map(app => 
          app.packageName === packageName ? { ...app, disabled: false } : app
        ));
      } else {
        message.error(response.data.message || '发送启用应用指令失败');
      }
    } catch (error) {
      message.error('发送启用应用指令失败');
      console.error('Show app error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 禁止卸载应用
  const handleBlockUninstall = async (packageName) => {
    setOperating(true);
    try {
      const response = await api.post('/commands/block-uninstall', {
        deviceId,
        packageName
      });

      if (response.data.success) {
        message.success('禁止卸载指令已发送');
        setApps(prev => prev.map(app => 
          app.packageName === packageName ? { ...app, uninstallBlocked: true } : app
        ));
      } else {
        message.error(response.data.message || '发送禁止卸载指令失败');
      }
    } catch (error) {
      message.error('发送禁止卸载指令失败');
      console.error('Block uninstall error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 允许卸载应用
  const handleUnblockUninstall = async (packageName) => {
    setOperating(true);
    try {
      const response = await api.post('/commands/unblock-uninstall', {
        deviceId,
        packageName
      });

      if (response.data.success) {
        message.success('允许卸载指令已发送');
        setApps(prev => prev.map(app => 
          app.packageName === packageName ? { ...app, uninstallBlocked: false } : app
        ));
      } else {
        message.error(response.data.message || '发送允许卸载指令失败');
      }
    } catch (error) {
      message.error('发送允许卸载指令失败');
      console.error('Unblock uninstall error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 卸载应用
  const handleUninstallApp = async (packageName) => {
    setUninstallingApp(true);
    try {
      const response = await api.post('/commands/uninstall', {
        deviceId,
        packageName
      });

      if (response.data.success) {
        message.success('卸载指令已发送');
        setApps(prev => prev.filter(app => app.packageName !== packageName));
      } else {
        message.error(response.data.message || '发送卸载指令失败');
      }
    } catch (error) {
      message.error('发送卸载指令失败');
      console.error('Uninstall app error:', error);
    } finally {
      setUninstallingApp(false);
    }
  };

  // 批量禁用
  const handleBatchHide = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要禁用的应用');
      return;
    }

    setOperating(true);
    try {
      const response = await api.post('/commands/batch-hide-apps', {
        deviceId,
        packageNames: selectedRowKeys
      });

      if (response.data.success) {
        message.success(`批量禁用指令已发送，共 ${selectedRowKeys.length} 个应用`);
        setApps(prev => prev.map(app => 
          selectedRowKeys.includes(app.packageName) ? { ...app, disabled: true } : app
        ));
        setSelectedRowKeys([]);
      } else {
        message.error(response.data.message || '发送批量禁用指令失败');
      }
    } catch (error) {
      message.error('发送批量禁用指令失败');
      console.error('Batch hide error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 批量启用
  const handleBatchShow = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要启用的应用');
      return;
    }

    setOperating(true);
    try {
      const response = await api.post('/commands/batch-show-apps', {
        deviceId,
        packageNames: selectedRowKeys
      });

      if (response.data.success) {
        message.success(`批量启用指令已发送，共 ${selectedRowKeys.length} 个应用`);
        setApps(prev => prev.map(app => 
          selectedRowKeys.includes(app.packageName) ? { ...app, disabled: false } : app
        ));
        setSelectedRowKeys([]);
      } else {
        message.error(response.data.message || '发送批量启用指令失败');
      }
    } catch (error) {
      message.error('发送批量启用指令失败');
      console.error('Batch show error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 批量禁止卸载
  const handleBatchBlockUninstall = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要禁止卸载的应用');
      return;
    }

    setOperating(true);
    try {
      const response = await api.post('/commands/batch-block-uninstall', {
        deviceId,
        packageNames: selectedRowKeys
      });

      if (response.data.success) {
        message.success(`批量禁止卸载指令已发送，共 ${selectedRowKeys.length} 个应用`);
        setApps(prev => prev.map(app => 
          selectedRowKeys.includes(app.packageName) ? { ...app, uninstallBlocked: true } : app
        ));
        setSelectedRowKeys([]);
      } else {
        message.error(response.data.message || '发送批量禁止卸载指令失败');
      }
    } catch (error) {
      message.error('发送批量禁止卸载指令失败');
      console.error('Batch block uninstall error:', error);
    } finally {
      setOperating(false);
    }
  };

  // 批量允许卸载
  const handleBatchUnblockUninstall = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要允许卸载的应用');
      return;
    }

    setOperating(true);
    try {
      const response = await api.post('/commands/batch-unblock-uninstall', {
        deviceId,
        packageNames: selectedRowKeys
      });

      if (response.data.success) {
        message.success(`批量允许卸载指令已发送，共 ${selectedRowKeys.length} 个应用`);
        setApps(prev => prev.map(app => 
          selectedRowKeys.includes(app.packageName) ? { ...app, uninstallBlocked: false } : app
        ));
        setSelectedRowKeys([]);
      } else {
        message.error(response.data.message || '发送批量允许卸载指令失败');
      }
    } catch (error) {
      message.error('发送批量允许卸载指令失败');
      console.error('Batch unblock uninstall error:', error);
    } finally {
      setOperating(false);
    }
  };

  const columns = [
    {
      title: '应用名称',
      dataIndex: 'appName',
      key: 'appName',
      ellipsis: true,
      render: (text) => text || '-'
    },
    {
      title: '包名',
      dataIndex: 'packageName',
      key: 'packageName',
      ellipsis: true
    },
    {
      title: '版本号',
      dataIndex: 'version',
      key: 'version',
      width: 100,
      render: (text) => text || '-'
    },
    {
      title: '安装时间',
      dataIndex: 'installDate',
      key: 'installDate',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString('zh-CN') : '未知'
    },
    {
      title: '应用状态',
      key: 'appStatus',
      width: 100,
      render: (_, record) => (
        <Tag color={record.disabled ? 'red' : 'green'}>
          {record.disabled ? '已禁用' : '已启用'}
        </Tag>
      )
    },
    {
      title: '卸载保护',
      key: 'uninstallProtected',
      width: 100,
      render: (_, record) => (
        <Tag color={record.uninstallBlocked ? 'orange' : 'default'}>
          {record.uninstallBlocked ? '已锁定' : '可卸载'}
        </Tag>
      )
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => (
        <Space size="small" wrap>
          {record.disabled ? (
            <Tooltip title="启用应用">
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                loading={operating}
                onClick={() => handleShowApp(record.packageName)}
              >
                启用
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title="确定要禁用这个应用吗？"
              description={`应用名称: ${record.appName}`}
              onConfirm={() => handleHideApp(record.packageName)}
              okText="确定"
              cancelText="取消"
            >
              <Tooltip title="禁用应用">
                <Button
                  size="small"
                  icon={<StopOutlined />}
                  loading={operating}
                >
                  禁用
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
          {record.uninstallBlocked ? (
            <Tooltip title="允许卸载">
              <Button
                size="small"
                icon={<UnlockOutlined />}
                loading={operating}
                onClick={() => handleUnblockUninstall(record.packageName)}
              >
                解锁
              </Button>
            </Tooltip>
          ) : (
            <Popconfirm
              title="确定要禁止卸载这个应用吗？"
              description={`应用名称: ${record.appName}`}
              onConfirm={() => handleBlockUninstall(record.packageName)}
              okText="确定"
              cancelText="取消"
            >
              <Tooltip title="禁止卸载">
                <Button
                  size="small"
                  icon={<LockOutlined />}
                  loading={operating}
                >
                  锁定
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
          <Popconfirm
            title={record.uninstallBlocked ? "此应用已锁定，确定要强制卸载吗？" : "确定要卸载这个应用吗？"}
            description={`应用名称: ${record.appName}`}
            onConfirm={() => handleUninstallApp(record.packageName)}
            okText="确定"
            cancelText="取消"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="卸载应用">
              <Button
                type="primary"
                danger
                size="small"
                icon={<DeleteOutlined />}
                loading={uninstallingApp}
              >
                卸载
              </Button>
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (newSelectedRowKeys) => setSelectedRowKeys(newSelectedRowKeys),
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
    ]
  };

  return (
    <div>
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Button type="link" onClick={() => navigate('/devices')} style={{ padding: 0 }}>
            设备管理
          </Button>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {device?.deviceName || deviceId}
        </Breadcrumb.Item>
        <Breadcrumb.Item>已安装应用</Breadcrumb.Item>
      </Breadcrumb>

      <Card
        title={
          <Space>
            <span>{device?.deviceName || deviceId} - 已安装应用</span>
            <Tag color="blue">{apps.length} 个应用</Tag>
            {device?.isDeviceOwner && <Tag color="green">设备所有者</Tag>}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/devices')}
            >
              返回设备列表
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchDeviceAndApps}
              loading={loading}
            >
              刷新
            </Button>
          </Space>
        }
      >
        {/* 搜索和筛选 */}
        <Space style={{ marginBottom: 16 }} wrap>
          <Search
            placeholder="搜索应用名称或包名"
            allowClear
            style={{ width: 300 }}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(value) => setSearchText(value)}
          />
          <Select
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 120 }}
          >
            <Option value="all">全部状态</Option>
            <Option value="enabled">已启用</Option>
            <Option value="disabled">已禁用</Option>
          </Select>
        </Space>

        {/* 批量操作 */}
        {selectedRowKeys.length > 0 && (
          <Card 
            size="small" 
            style={{ marginBottom: 16, background: '#f5f5f5' }}
          >
            <Space>
              <span>已选择 <strong>{selectedRowKeys.length}</strong> 个应用</span>
              <Popconfirm
                title={`确定要批量禁用 ${selectedRowKeys.length} 个应用吗？`}
                onConfirm={handleBatchHide}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  icon={<StopOutlined />}
                  loading={operating}
                >
                  批量禁用
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`确定要批量启用 ${selectedRowKeys.length} 个应用吗？`}
                onConfirm={handleBatchShow}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={operating}
                >
                  批量启用
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`确定要批量禁止卸载 ${selectedRowKeys.length} 个应用吗？`}
                onConfirm={handleBatchBlockUninstall}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  icon={<LockOutlined />}
                  loading={operating}
                >
                  批量锁定
                </Button>
              </Popconfirm>
              <Popconfirm
                title={`确定要批量允许卸载 ${selectedRowKeys.length} 个应用吗？`}
                onConfirm={handleBatchUnblockUninstall}
                okText="确定"
                cancelText="取消"
              >
                <Button
                  icon={<UnlockOutlined />}
                  loading={operating}
                >
                  批量解锁
                </Button>
              </Popconfirm>
              <Button onClick={() => setSelectedRowKeys([])}>
                取消选择
              </Button>
            </Space>
          </Card>
        )}

        <Table
          dataSource={filteredApps}
          columns={columns}
          rowKey="packageName"
          loading={loading}
          rowSelection={rowSelection}
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 个应用`
          }}
        />
      </Card>
    </div>
  );
};

export default InstalledAppsList;
